import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { FeatureGridIcon } from '../blocks/FeatureGridIcon';
import { FeatureIconVisual } from '../blocks/FeatureIconVisual';
import {
  FEATURE_ICON_CATEGORIES,
  FEATURE_ICON_PACK,
  type FeatureIconCategory,
} from '../utils/featureIconPack';
import {
  isBuiltInIconKey,
  isImageIconUrl,
  resolveFeatureIconDisplay,
  type FeatureItemIcon,
} from '../utils/featuresGridUtils';
import { storageService } from '../../../services/storageService';

type PickerPanel = 'pack' | 'upload' | 'emoji' | null;

export type FeatureIconPickerProps = {
  feature: FeatureItemIcon;
  onChange: (updates: { icon?: string; iconUrl?: string }) => void;
  allowEmoji?: boolean;
  /** Show control to clear icon and use step numbers only (numbered layout). */
  allowClearIcon?: boolean;
  currentUserId?: string;
  workspaceId?: string;
};

const FeatureIconPackModal: React.FC<{
  selectedPackId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
}> = ({ selectedPackId, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<FeatureIconCategory | 'all'>('all');

  const filteredPack = useMemo(() => {
    const q = search.trim().toLowerCase();
    return FEATURE_ICON_PACK.filter((icon) => {
      if (category !== 'all' && icon.category !== category) return false;
      if (!q) return true;
      return icon.label.toLowerCase().includes(q) || icon.id.toLowerCase().includes(q);
    });
  }, [search, category]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-label="Icon pack library"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[min(85vh,640px)] flex flex-col overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Icon pack library</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {FEATURE_ICON_PACK.length} icons · browse and select
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 pt-3 pb-2 space-y-2 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search icons..."
            autoFocus
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
          <div className="flex gap-1 flex-wrap max-h-16 overflow-y-auto custom-scrollbar">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`px-2 py-1 text-[11px] font-medium rounded-full border ${
                category === 'all'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              All
            </button>
            {FEATURE_ICON_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                className={`px-2 py-1 text-[11px] font-medium rounded-full border ${
                  category === cat.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 custom-scrollbar">
          <div className="grid grid-cols-8 gap-1.5">
            {filteredPack.map((icon) => (
              <button
                key={icon.id}
                type="button"
                title={icon.label}
                onClick={() => onSelect(icon.id)}
                className={`aspect-square rounded-lg border flex items-center justify-center bg-white hover:border-slate-500 transition-colors ${
                  selectedPackId === icon.id
                    ? 'border-slate-900 ring-2 ring-slate-200 bg-slate-50'
                    : 'border-slate-200'
                }`}
              >
                <FeatureGridIcon iconKey={icon.id} size={20} color="#111827" />
              </button>
            ))}
            {filteredPack.length === 0 && (
              <p className="col-span-8 text-center text-xs text-slate-400 py-8">No icons found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeatureIconPicker: React.FC<FeatureIconPickerProps> = ({
  feature,
  onChange,
  allowEmoji = false,
  allowClearIcon = false,
  currentUserId,
  workspaceId,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [openPanel, setOpenPanel] = useState<PickerPanel>(null);
  const [uploading, setUploading] = useState(false);

  const display = resolveFeatureIconDisplay(feature);
  const selectedPackId: string | null =
    display?.kind === 'pack'
      ? display.key
      : isBuiltInIconKey(feature.icon)
        ? feature.icon || null
        : null;

  const selectPackIcon = (id: string) => {
    onChange({ icon: id, iconUrl: '' });
    setOpenPanel(null);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentUserId) return;
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, SVG, JPG, etc.).');
      return;
    }
    setUploading(true);
    try {
      const uploaded = await storageService.uploadFile(file, currentUserId, workspaceId);
      onChange({ iconUrl: uploaded.url, icon: '' });
      setOpenPanel('upload');
    } catch {
      alert('Failed to upload icon. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const sourceButtons: { id: PickerPanel; label: string }[] = [
    { id: 'pack', label: 'Icon pack' },
    { id: 'upload', label: 'Upload' },
    ...(allowEmoji ? [{ id: 'emoji' as PickerPanel, label: 'Emoji' }] : []),
  ];

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpenPanel('pack')}
        className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 transition-colors text-left"
      >
        <div className="w-11 h-11 rounded-lg border border-slate-200 bg-white flex items-center justify-center shrink-0">
          {display ? (
            <FeatureIconVisual feature={feature} size={26} emojiFontSize={22} />
          ) : (
            <span className="text-xs text-slate-400">None</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-800">
            {display ? 'Current icon' : 'Choose an icon'}
          </p>
          <p className="text-[11px] text-slate-500 truncate">
            {display?.kind === 'pack'
              ? FEATURE_ICON_PACK.find((i) => i.id === display.key)?.label || display.key
              : display?.kind === 'image'
                ? 'Custom upload'
                : display?.kind === 'emoji'
                  ? 'Emoji'
                  : 'Click to browse icon pack'}
          </p>
        </div>
        <span className="text-[11px] font-semibold text-slate-600 shrink-0">Browse</span>
      </button>

      {allowClearIcon && display && (
        <button
          type="button"
          onClick={() => onChange({ icon: '', iconUrl: '' })}
          className="w-full py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          Use step numbers only
        </button>
      )}

      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
        {sourceButtons.map((btn) => (
          <button
            key={btn.id}
            type="button"
            onClick={() => {
              if (btn.id === 'pack') {
                setOpenPanel('pack');
              } else {
                setOpenPanel(openPanel === btn.id ? null : btn.id);
              }
            }}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              openPanel === btn.id
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {openPanel === 'pack' && (
        <FeatureIconPackModal
          selectedPackId={selectedPackId}
          onSelect={selectPackIcon}
          onClose={() => setOpenPanel(null)}
        />
      )}

      {openPanel === 'upload' && (
        <div className="space-y-2">
          <button
            type="button"
            disabled={uploading || !currentUserId}
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium border border-dashed border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <ArrowUpOnSquareIcon className="w-4 h-4 text-slate-500" />
            {uploading ? 'Uploading...' : 'Upload icon image'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.svg"
            className="hidden"
            onChange={handleUpload}
          />
          <p className="text-[11px] text-slate-500">
            PNG, JPG, SVG, or WebP. Recommended size: 64×64px or larger.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Or paste image URL</label>
            <input
              type="text"
              value={feature.iconUrl || (isImageIconUrl(feature.icon) ? feature.icon : '') || ''}
              onChange={(e) => onChange({ iconUrl: e.target.value.trim(), icon: '' })}
              placeholder="https://..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20"
            />
          </div>
        </div>
      )}

      {openPanel === 'emoji' && allowEmoji && (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Emoji or text</label>
          <input
            type="text"
            value={display?.kind === 'emoji' ? display.text : ''}
            onChange={(e) => onChange({ icon: e.target.value, iconUrl: '' })}
            placeholder="⚡"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20"
          />
        </div>
      )}
    </div>
  );
};
